import axios from "axios";
import { extend } from "quasar";
import * as Api from "src/api";
import { CancelToken } from "assets/scripts/utilities/api-clients";
import { getProperty } from "src/assets/scripts/utilities/helpers";

const appendCollection = ({
  existingEntityIds,
  collection,
  response = {},
} = {}) => {
  response = response?.data?.data?.data || response?.data?.data || [];

  for (const entity of response) {
    if (!unref(existingEntityIds)?.has(entity._id)) {
      collection.value.push(entity);
    }
  }
};

const fetchEntities = async ({
  abort = () => {},
  defaultOptions,
  entity,
  getter,
} = {}) => {
  try {
    let response;

    if (getter) {
      response = await getter(defaultOptions);
    } else {
      response = await Api[entity]?.index(defaultOptions);
    }

    return response;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.warn(error.message);
    } else {
      console.error(error);

      abort();
    }
  }
};

const filterEntities = ({ entities, props = ["name"], query } = {}) => {
  if (query && query.length < 3) {
    return entities;
  }

  return entities.filter((entity) =>
    props.reduce(
      (includes, prop) =>
        includes ||
        getProperty(entity, prop)
          ?.toLowerCase?.()
          .includes(query?.toLowerCase?.()),

      false,
    ),
  );
};

const updateCollection = ({
  collection,
  count,
  entities,
  props,
  query,
  response,
  totalPages,
} = {}) => {
  if (entities?.length) {
    if (query) {
      collection.value = filterEntities({ entities, props, query });
    } else {
      collection.value = entities;
    }
  } else {
    if (response?.data?.data?.data) {
      response = response.data;
    }

    count.value = response?.data?.count;

    collection.value = response?.data?.data;

    totalPages.value = response?.data?.total_pages;
  }
};

export function useFilter({
  entities = [],
  entity,
  getter,
  options = {},
  props,
  pageSize = 25,
} = {}) {
  entities = unref(entities);

  const collection = ref([...entities]);

  const loading = ref(false);

  const count = ref(0);
  const nextPage = ref(1);
  const totalPages = ref(0);

  const existingEntityIds = computed(() => {
    return new Set(collection.value.map((entity) => entity._id));
  });

  let source;

  source = CancelToken.source();

  let defaultOptions = {
    cancelToken: source?.token,
    params: {
      page_size: pageSize,
      offset: null,
      search: null,
      sortBy: "created_date",
      orderBy: "desc",
    },
  };

  if (props) {
    defaultOptions.params.search_fields = props?.join(",");
  }

  defaultOptions = extend(true, {}, defaultOptions, options);

  const onFilter = async (query = null, update, abort, overrideParams = {}) => {
    onFilterAbort();

    nextPage.value = 1;

    defaultOptions.params = {
      ...defaultOptions.params,
      ...overrideParams,
      search: query,
      offset: null,
    };

    let response;

    if (!entities?.length) {
      response = await fetchEntities({
        abort,
        defaultOptions,
        entity,
        getter,
        loading,
      });
    }

    update(() =>
      updateCollection({
        collection,
        count,
        entities,
        props,
        query,
        response,
        totalPages,
      }),
    );
  };

  const onScroll = async ({ index, to, ref }) => {
    if (index <= 0) return;

    const lastIndex = unref(collection)?.length - 1 || -1;
    const lastPage = unref(totalPages);

    if (
      loading.value !== true &&
      nextPage.value < lastPage &&
      to === lastIndex
    ) {
      nextPage.value++;

      defaultOptions.params.offset = pageSize * (nextPage.value - 1);

      loading.value = true;

      const response = await fetchEntities({
        entity,
        defaultOptions,
        loading,
        getter,
      });

      appendCollection({
        existingEntityIds,
        collection,
        response,
      });

      nextTick(() => {
        ref.refresh();

        loading.value = false;
      });
    }
  };

  const onFilterAbort = () => {
    source?.cancel("Request Cancelled");

    source = CancelToken.source();

    defaultOptions.cancelToken = source?.token;
  };

  return {
    loading,
    entities: collection,
    filterEntities,
    onFilter,
    onFilterAbort,
    onScroll,
  };
}
